import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, AlertCircle } from 'lucide-react';
import { apiFetch } from '@/utils/api';
import { useI18n } from '@/hooks/useI18n';

interface TagItem {
    id: string;
    name: string;
    usage_count?: number;
}

export default function AdminTagsPage() {
    const { t } = useI18n();
    const [tags, setTags] = useState<TagItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [createName, setCreateName] = useState('');
    const [editName, setEditName] = useState('');
    const [editingTag, setEditingTag] = useState<TagItem | null>(null);

    useEffect(() => {
        void loadTags();
    }, []);

    const loadTags = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await apiFetch('/api/tags?include_usage=true');
            if (!response.ok) {
                setError(t('tagsAdmin.errors.load', 'common'));
                return;
            }
            const data = await response.json();
            setTags(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Failed to load tags:', err);
            setError(t('tagsAdmin.errors.load', 'common'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreate = async () => {
        if (!createName.trim()) {
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            const response = await apiFetch('/api/tags', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: createName.trim(),
                }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                setError(errorData.detail || t('tagsAdmin.errors.create', 'common'));
                return;
            }

            setIsCreateDialogOpen(false);
            setCreateName('');
            await loadTags();
        } catch (err) {
            console.error('Failed to create tag:', err);
            setError(t('tagsAdmin.errors.create', 'common'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const openEditModal = (tag: TagItem) => {
        setEditingTag(tag);
        setEditName(tag.name);
        setIsEditDialogOpen(true);
    };

    const closeEditModal = () => {
        setIsEditDialogOpen(false);
        setEditingTag(null);
        setEditName('');
    };

    const handleUpdate = async () => {
        if (!editingTag || !editName.trim()) {
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            const response = await apiFetch(`/api/tags/${editingTag.id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: editName.trim(),
                }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                setError(errorData.detail || t('tagsAdmin.errors.update', 'common'));
                return;
            }

            closeEditModal();
            await loadTags();
        } catch (err) {
            console.error('Failed to update tag:', err);
            setError(t('tagsAdmin.errors.update', 'common'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (tagId: string) => {
        if (!confirm(t('tagsAdmin.confirmDelete', 'common'))) {
            return;
        }

        setError(null);
        try {
            const response = await apiFetch(`/api/tags/${tagId}`, {
                method: 'DELETE',
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                setError(errorData.detail || t('tagsAdmin.errors.delete', 'common'));
                return;
            }

            await loadTags();
        } catch (err) {
            console.error('Failed to delete tag:', err);
            setError(t('tagsAdmin.errors.delete', 'common'));
        }
    };

    return (
        <Layout>
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">{t('tagsAdmin.title', 'common')}</h1>
                        <p className="text-gray-600">{t('tagsAdmin.description', 'common')}</p>
                    </div>
                    <Button onClick={() => setIsCreateDialogOpen(true)}>
                        <Plus className="h-4 w-4 mr-2" />
                        {t('tagsAdmin.newTag', 'common')}
                    </Button>
                </div>

                {error && (
                    <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                <div className="rounded-md border overflow-hidden bg-white">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t('tagsAdmin.tagName', 'common')}</TableHead>
                                <TableHead>{t('tagsAdmin.inUse', 'common')}</TableHead>
                                <TableHead className="text-right">{t('actions.actions', 'common')}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                                        {t('actions.loading', 'common')}
                                    </TableCell>
                                </TableRow>
                            ) : tags.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                                        {t('tagsAdmin.emptyState', 'common')}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                tags.map((tag) => {
                                    const usageCount = tag.usage_count || 0;
                                    const isInUse = usageCount > 0;
                                    return (
                                        <TableRow
                                            key={tag.id}
                                            className="cursor-pointer"
                                            onClick={() => openEditModal(tag)}
                                        >
                                            <TableCell className="font-medium">{tag.name}</TableCell>
                                            <TableCell>
                                                {isInUse ? (
                                                    <Badge variant="default">{t('actions.yes', 'common')} ({usageCount})</Badge>
                                                ) : (
                                                    <Badge variant="secondary">{t('actions.no', 'common')}</Badge>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        void handleDelete(tag.id);
                                                    }}
                                                >
                                                    <Trash2 className="h-4 w-4 text-red-600" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </div>

                <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                    <DialogContent className="max-w-2xl">
                        <DialogHeader>
                            <DialogTitle>{t('tagsAdmin.createTitle', 'common')}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-2 py-2">
                            <Label htmlFor="create-tag-name">{t('tagsAdmin.tagNameLabel', 'common')}</Label>
                            <Input
                                id="create-tag-name"
                                value={createName}
                                onChange={(e) => setCreateName(e.target.value)}
                                placeholder={t('tagsAdmin.tagNamePlaceholder', 'common')}
                            />
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)} disabled={isSubmitting}>
                                {t('actions.cancel', 'common')}
                            </Button>
                            <Button onClick={() => void handleCreate()} disabled={isSubmitting || !createName.trim()}>
                                {t('actions.create', 'common')}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                <Dialog open={isEditDialogOpen} onOpenChange={(open) => {
                    if (!open) {
                        closeEditModal();
                    } else {
                        setIsEditDialogOpen(true);
                    }
                }}>
                    <DialogContent className="max-w-2xl">
                        <DialogHeader>
                            <DialogTitle>{t('tagsAdmin.editTitle', 'common')}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-2 py-2">
                            <Label htmlFor="edit-tag-name">{t('tagsAdmin.tagNameLabel', 'common')}</Label>
                            <Input
                                id="edit-tag-name"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                placeholder={t('tagsAdmin.tagNamePlaceholder', 'common')}
                            />
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={closeEditModal} disabled={isSubmitting}>
                                {t('actions.cancel', 'common')}
                            </Button>
                            <Button onClick={() => void handleUpdate()} disabled={isSubmitting || !editName.trim()}>
                                {t('actions.save', 'common')}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
}

export const Route = createFileRoute('/admin/tags')({
    component: AdminTagsPage,
});
